import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Copy,
  Check,
  Send,
  Link as LinkIcon,
  MessageSquareText,
  Settings,
  ExternalLink,
} from "lucide-react";

const DEFAULT_TEMPLATE =
  "Hola {{nombre}}, te enviamos el enlace para dar de alta tu expediente en Natur Autoescuela. Complétalo desde tu móvil (te pediremos foto del DNI): {{url}}";

function renderTemplate(tpl, ctx) {
  return (tpl || DEFAULT_TEMPLATE)
    .replace(/\{\{\s*nombre\s*\}\}/gi, ctx.nombre || "")
    .replace(/\{\{\s*url\s*\}\}/gi, ctx.url || "");
}

function normalizePhoneForWa(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[^\d]/g, "");
  if (!digits) return "";
  // Assume Spain if 9 digits without country code
  if (digits.length === 9) return "34" + digits;
  return digits;
}

export default function AltaDigitalLinkDialog({ open, student, onClose }) {
  const [loading, setLoading] = useState(false);
  const [portalUrl, setPortalUrl] = useState("");
  const [tokenInfo, setTokenInfo] = useState(null);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateDraft, setTemplateDraft] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [copied, setCopied] = useState(false);

  const studentId = student?.id;
  const studentName = student?.name || "";
  const studentPhone = student?.phone || "";

  const generate = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const [linkRes, tplRes] = await Promise.all([
        api.post(`/students/${studentId}/alta-link`, { channel: "whatsapp" }),
        api.get("/settings/alta_wa_template").catch(() => ({ data: { value: "" } })),
      ]);
      setPortalUrl(linkRes.data.portal_url);
      setTokenInfo(linkRes.data);
      setTemplate(tplRes.data?.value?.trim() || DEFAULT_TEMPLATE);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (open && studentId) {
      setPortalUrl("");
      setTokenInfo(null);
      setEditingTemplate(false);
      setCopied(false);
      generate();
    }
  }, [open, studentId, generate]);

  const finalMessage = renderTemplate(template, {
    nombre: studentName.split(" ")[0] || studentName,
    url: portalUrl,
  });

  const copyLink = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const openWhatsApp = () => {
    if (!portalUrl) return;
    const phone = normalizePhoneForWa(studentPhone);
    const encoded = encodeURIComponent(finalMessage);
    const url = phone
      ? `https://wa.me/${phone}?text=${encoded}`
      : `https://wa.me/?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const startEditTemplate = () => {
    setTemplateDraft(template);
    setEditingTemplate(true);
  };

  const saveTemplate = async () => {
    const v = templateDraft.trim();
    if (!v) return;
    setSavingTemplate(true);
    try {
      await api.patch("/settings/alta_wa_template", { value: v });
      setTemplate(v);
      setEditingTemplate(false);
      toast.success("Plantilla guardada");
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 440 && status !== 428) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="alta-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-orange-500" />
            Enviar ficha digital
          </DialogTitle>
          <DialogDescription className="sr-only">
            Genera un enlace público de alta digital y compártelo con el alumno por WhatsApp o copia el enlace.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-orange-500 mx-auto" />
            <div className="text-xs text-neutral-500 mt-2">Generando enlace…</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 mb-1">
                Enlace público
              </div>
              <div className="font-mono text-xs break-all text-neutral-800 bg-white border border-neutral-200 rounded-lg px-2 py-1.5" data-testid="alta-link-url">
                {portalUrl || "—"}
              </div>
              {tokenInfo?.expires_at && (
                <div className="text-[11px] text-neutral-500 mt-1.5">
                  Caduca:{" "}
                  <span className="font-semibold text-neutral-700">
                    {new Date(tokenInfo.expires_at).toLocaleDateString("es-ES")}
                  </span>
                </div>
              )}
            </div>

            {/* Editable template */}
            <div className="rounded-xl border border-neutral-200 bg-white p-3">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquareText className="h-4 w-4 text-orange-500" />
                <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 flex-1">
                  Mensaje WhatsApp
                </div>
                {!editingTemplate && (
                  <button
                    type="button"
                    onClick={startEditTemplate}
                    className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
                    data-testid="alta-template-edit"
                    title="Editar plantilla"
                  >
                    <Settings className="h-3.5 w-3.5" /> Editar
                  </button>
                )}
              </div>

              {editingTemplate ? (
                <div className="space-y-2">
                  <textarea
                    value={templateDraft}
                    onChange={(e) => setTemplateDraft(e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="alta-template-input"
                  />
                  <div className="text-[10px] text-neutral-500">
                    Placeholders soportados: <code>{"{{nombre}}"}</code>, <code>{"{{url}}"}</code>. Se guarda como plantilla predeterminada (PIN maestro).
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setEditingTemplate(false)}
                      className="btn-ghost !h-8 !text-xs border border-neutral-200"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={saveTemplate}
                      disabled={savingTemplate || !templateDraft.trim()}
                      className="btn-primary !h-8 !text-xs"
                      data-testid="alta-template-save"
                    >
                      <Check className="h-3.5 w-3.5" />
                      {savingTemplate ? "Guardando…" : "Guardar"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-2 py-1.5 text-xs text-neutral-700 whitespace-pre-wrap leading-relaxed">
                  {finalMessage}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={copyLink}
                disabled={!portalUrl}
                className="h-11 rounded-xl border border-neutral-300 bg-white hover:bg-neutral-50 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="alta-copy-link"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copiado" : "Copiar enlace"}
              </button>
              <button
                type="button"
                onClick={openWhatsApp}
                disabled={!portalUrl}
                className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="alta-open-whatsapp"
              >
                <Send className="h-4 w-4" />
                WhatsApp
              </button>
            </div>

            {portalUrl && (
              <div className="text-[11px] text-neutral-500 flex items-center gap-1.5 justify-center pt-1">
                <ExternalLink className="h-3 w-3" />
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-neutral-800 underline decoration-dotted"
                  data-testid="alta-preview-link"
                >
                  Ver la ficha como la verá el alumno
                </a>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
